import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, money, fmtDate, statusClass, statusLabel, CASE_STATUSES } from "@/lib/api";
import { PageHeader, Badge } from "@/components/Shared";

const Stat = ({ label, value, sub, testId }) => (
  <div className="border border-border bg-card p-5" data-testid={testId}>
    <p className="label-caps">{label}</p>
    <p className="font-display text-3xl mt-2">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </div>
);

const LIM = { running: "bg-slate-100 text-slate-800", warning: "bg-amber-50 text-amber-900", critical: "bg-red-50 text-red-900", expired: "bg-red-100 text-red-900" };

export default function AdminOverview() {
  const [a, setA] = useState(null);
  const [cases, setCases] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setA(data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/cases", { params: { q: q || undefined, status: status || undefined } })
      .then(({ data }) => setCases(data)).catch((e) => toast.error(errMsg(e))), 250);
    return () => clearTimeout(t);
  }, [q, status]);

  return (
    <div data-testid="admin-overview-page">
      <PageHeader eyebrow="Admin" title="Registry Overview" description="Every reference on the registry with its tribunal, stage, payment and limitation position — read-only." testId="admin-overview-header" />
      {a && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="admin-analytics">
          <Stat label="Cases filed" value={a.total_cases} sub={`${a.cases_this_month} this month`} testId="stat-cases" />
          <Stat label="Revenue collected" value={money(a.revenue_collected)} sub={`${money(a.revenue_this_month)} this month`} testId="stat-revenue" />
          <Stat label="Active arbitrators" value={a.arbitrators.active} sub={`${a.arbitrators.pending} pending · ${a.arbitrators.deactivated} deactivated`} testId="stat-arbitrators" />
          <Stat label="Pending payments" value={money(a.pending_payments.amount)} sub={`${a.pending_payments.count} fee note(s)`} testId="stat-pending" />
          <Stat label="Upcoming sittings" value={a.sittings_upcoming} sub={`${a.advocates} advocates · ${a.clients} clients`} testId="stat-sittings" />
        </div>
      )}
      {a && (
        <div className="mt-4 border border-border bg-card p-5 flex flex-wrap gap-3" data-testid="cases-by-status">
          <p className="label-caps w-full">Cases by status</p>
          {CASE_STATUSES.map((s) => (
            <button key={s.value} onClick={() => setStatus(status === s.value ? "" : s.value)} data-testid={`status-chip-${s.value}`}
              className={`px-3 py-1.5 text-xs border transition-colors duration-200 ${status === s.value ? "bg-blue-900 text-white border-blue-900" : "border-border hover:bg-accent"}`}>
              {s.label} <span className="font-mono ml-1">{a.cases_by_status[s.value] || 0}</span>
            </button>
          ))}
        </div>
      )}

      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by file number, reference, party or title…" data-testid="admin-case-search"
        className="mt-8 w-full h-10 px-3 border border-border bg-background text-sm rounded-none" />
      <div className="mt-4 border border-border bg-card overflow-x-auto">
        {!cases ? <div className="h-40 animate-pulse bg-muted" /> : cases.length === 0 ? (
          <p className="p-10 text-sm text-muted-foreground" data-testid="admin-cases-empty">No references match.</p>
        ) : (
          <table className="w-full text-sm min-w-[980px]">
            <thead><tr className="border-b border-border bg-muted/60">
              {["Reference", "Parties", "Tribunal", "Stage", "Fee halves", "Limitation", "Filed"].map((h) => <th key={h} className="p-4 text-left label-caps font-normal">{h}</th>)}
            </tr></thead>
            <tbody>
              {cases.map((c) => (
                <tr key={c.id} className="border-b border-border last:border-0" data-testid={`admin-case-row-${c.ref_id}`}>
                  <td className="p-4"><p className="font-mono text-xs">{c.case_number || c.ref_id}</p><p className="text-xs text-muted-foreground">{c.title}</p></td>
                  <td className="p-4 text-xs">{c.claimant}<br /><span className="text-muted-foreground">v. {c.respondent}</span></td>
                  <td className="p-4 text-xs">{c.tribunal.length ? c.tribunal.join(", ") : "—"} <span className="text-muted-foreground">({c.tribunal.length}/{c.seats})</span></td>
                  <td className="p-4"><Badge className={statusClass(c.status)}>{statusLabel(c.status)}</Badge></td>
                  <td className="p-4 text-xs">
                    <span className={c.payment.claimant ? "text-green-800" : "text-amber-800"}>C {c.payment.claimant ? "paid" : "due"}</span> ·{" "}
                    <span className={c.payment.respondent ? "text-green-800" : "text-amber-800"}>R {c.payment.respondent ? "paid" : "due"}</span>
                  </td>
                  <td className="p-4">{c.limitation ? <Badge className={LIM[c.limitation.state]}>{c.limitation.days_left < 0 ? "expired" : `${c.limitation.days_left}d · ${fmtDate(c.limitation.deadline)}`}</Badge> : <span className="text-xs text-muted-foreground">not started</span>}</td>
                  <td className="p-4 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(c.created_at)}<br /><Link to={`/cases/${c.id}`} className="text-blue-900 dark:text-blue-400 hover:underline">audit</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
